import { queryOptions } from '@tanstack/react-query';
import type { BoardColumn, Priority, SprintState, WorkType } from '@rundown/types';
import { api } from '../../lib/api';

/**
 * Board and time-entry transport.
 *
 * ── ASSUMED CONTRACT ────────────────────────────────────────────────────────
 *   GET   /workspaces/:workspaceId/board                                  -> BoardList
 *   PATCH /workspaces/:workspaceId/board/:requestId/move                   { column, beforeId?, afterId? } -> BoardCard
 *   GET   /workspaces/:workspaceId/requests/:requestId/time-entries        -> TimeEntry[]
 *   POST  /workspaces/:workspaceId/requests/:requestId/time-entries        { userId, minutes, type, occurredOn, note? } -> TimeEntry
 *   GET   /workspaces/:workspaceId/time-entries/roster                     -> EngineerOption[]
 *
 * Query keys for this feature are local (`boardKeys`/`timeKeys` below) rather
 * than added to lib/queryKeys.ts, which another agent owns concurrently in
 * this same tree — see backlog/api.ts for the identical reasoning. Every key
 * still starts `['ws', workspaceId, …]` per that file's own HARD RULE, so
 * useRealtime.ts's prefix-based invalidation still reaches them.
 */

/** Two bands. The old `unscheduled` lane is gone — see board.service.ts's
 * `BoardLane` for what it duplicated and what replaced it. */
export type BoardLane = 'hotfix' | 'sprint';

/** No client projection — see board.service.ts's header comment. Every field
 * here is safe for anyone who can reach the Board at all, because reaching it
 * at all already requires `board.write`, and nobody holds that without also
 * holding `internal.read`. */
export interface BoardCard {
  readonly id: string;
  readonly number: number;
  readonly title: string;
  readonly type: WorkType;
  readonly projectName: string | null;
  readonly priority: Priority;
  readonly approxHours: number;
  readonly loggedHours: number;
  readonly boardColumn: BoardColumn;
  readonly boardRank: string;
  /** `sprint` is the STARTED sprint only; a card committed to one merely locked is not on this screen yet. */
  readonly lane: BoardLane;
  readonly sprintId: string | null;
  readonly ownerId: string | null;
  readonly ownerName: string | null;
  readonly needsReview: boolean;
  readonly isHotfix: boolean;
  readonly visibleToClient: boolean;
}

/** The started sprint, sent with the cards so the band header needs no second
 * query — and cannot show a different sprint from the one the cards came from. */
export interface BoardSprint {
  readonly id: string;
  readonly name: string;
  readonly state: SprintState;
  readonly startsOn: string;
  readonly endsOn: string;
}

export interface BoardList {
  readonly cards: readonly BoardCard[];
  /** Null when no sprint is running. */
  readonly sprint: BoardSprint | null;
  /**
   * Settled, non-hotfix requests in a sprint-less in_progress/review/staging —
   * work in flight nobody committed to. A count rather than a lane because the
   * honest answer is zero; see board.service.ts's `outsideThePlanCount`.
   */
  readonly outsideThePlan: number;
  /** Hours held for urgent work, and how many are already spent. */
  readonly reserveHours: number;
  readonly hotfixHours: number;
}

export interface TimeEntry {
  readonly id: string;
  readonly requestId: string;
  /** Who did the work. A person — there is no separate engineer record. */
  readonly userId: string;
  readonly userName: string;
  readonly minutes: number;
  readonly type: WorkType;
  /** Date-only, e.g. "2026-09-16". */
  readonly occurredOn: string;
  readonly note: string | null;
  readonly loggedByName: string | null;
  readonly createdAt: string;
}

/** Someone hours can be logged against on this client: an active member with
 * a grade. See TimeService.roster. */
export interface EngineerOption {
  readonly id: string;
  readonly name: string;
  /** The caller's own row, resolved server-side — what the log form
   * defaults to. */
  readonly isSelf: boolean;
}

function boardPath(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/board`;
}

function timeEntriesPath(workspaceId: string, requestId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/requests/${encodeURIComponent(requestId)}/time-entries`;
}

export function fetchBoard(workspaceId: string, signal?: AbortSignal): Promise<BoardList> {
  return api.get<BoardList>(boardPath(workspaceId), signal);
}

export interface MoveCardInput {
  readonly column: BoardColumn;
  readonly beforeId?: string;
  readonly afterId?: string;
}

export function moveCard(workspaceId: string, requestId: string, input: MoveCardInput): Promise<BoardCard> {
  return api.patch<BoardCard>(`${boardPath(workspaceId)}/${encodeURIComponent(requestId)}/move`, input);
}

export function fetchTimeEntries(
  workspaceId: string,
  requestId: string,
  signal?: AbortSignal,
): Promise<TimeEntry[]> {
  return api.get<TimeEntry[]>(timeEntriesPath(workspaceId, requestId), signal);
}

export interface LogTimeInput {
  readonly userId: string;
  readonly minutes: number;
  /** Omitted: the server uses the request's own type. */
  readonly type?: WorkType;
  readonly occurredOn: string;
  readonly note?: string;
}

export function logTime(workspaceId: string, requestId: string, input: LogTimeInput): Promise<TimeEntry> {
  return api.post<TimeEntry>(timeEntriesPath(workspaceId, requestId), input);
}

export function fetchEngineerRoster(workspaceId: string, signal?: AbortSignal): Promise<EngineerOption[]> {
  return api.get<EngineerOption[]>(
    `/workspaces/${encodeURIComponent(workspaceId)}/time-entries/roster`,
    signal,
  );
}

/** Every key here starts `['ws', workspaceId]` — see lib/queryKeys.ts's HARD
 * RULE, which this local set still follows even though it is not declared in
 * that shared file (same footnote as backlog/api.ts's `backlogKeys`). */
export const boardKeys = {
  list: (workspaceId: string) => ['ws', workspaceId, 'board'] as const,
};

export const timeKeys = {
  forRequest: (workspaceId: string, requestId: string) =>
    ['ws', workspaceId, 'requests', requestId, 'time-entries'] as const,
  roster: (workspaceId: string) => ['ws', workspaceId, 'time-entries', 'roster'] as const,
};

export function boardQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: boardKeys.list(workspaceId),
    queryFn: ({ signal }) => fetchBoard(workspaceId, signal),
  });
}

export function timeEntriesQueryOptions(workspaceId: string, requestId: string) {
  return queryOptions({
    queryKey: timeKeys.forRequest(workspaceId, requestId),
    queryFn: ({ signal }) => fetchTimeEntries(workspaceId, requestId, signal),
  });
}

export function engineerRosterQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: timeKeys.roster(workspaceId),
    // The roster changes about as often as the team does — staleTime keeps a
    // picker that is opened and closed repeatedly from refetching every time.
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => fetchEngineerRoster(workspaceId, signal),
  });
}
